import React, {useEffect, useState} from "react";
import {useLocation, useNavigate, useParams} from "react-router-dom";
import {getFlatCode, isEmpty} from "../../../components/commonmethod/CommonMethod";
import {
    ContentDetailInfoShell,
    ContentDetailThreeColLayout,
    DetailFilePanel,
    DetailFormTabChrome,
    DetailKvTable,
    DetailPanel,
    DetailSummaryButton,
} from "../../../components/detailLayout";
import "../../../components/detailLayout/detailListLayout.css";
import Th_title from "../../../components/Th_title";
import ApprovalComponent from "../../settings/approval/new/ApprovalComponent";
import {useDispatch, useSelector} from "react-redux";
import {
    changeProgressStatusValue,
    changeStatusNumericValue,
} from "../../../redux/slice/contentSlice";
import {
    clearApprovalProcessState,
    getApprovalProcessSubmitPayload,
    preloadApprovalProcessFromFlatCode,
} from "../../settings/approval/shared/approvalProcessStore";
import {CodeSpecNum, ContentCategory} from "../../../constant/CodeSpecNum";
import {getContentStatus} from "../../../api/contentStatus/contentStatusService";
import {apiLoading, skeleton} from "../../../redux/slice/apiSlice";
import {onCreateClickListener} from "../../../components/commonmethod/simmsDrawerMethod";
import {inputOnChange} from "../../../components/commonmethod/simmsInputMethod";
import Selector from "../../../components/selector/Selector";
import SimmsDatePicker from "../../../components/datepicker/SimmsDatePicker";
import {dateOnChange} from "../../../components/commonmethod/simmsDateMethod";
import dayjs from "dayjs";
import SimmsInput from "../../../components/input/SimmsInput";
import SimmsTextArea from "../../../components/input/SimmsTextArea";
import ReadOnlyInputBasic from "../../../components/input/ReadOnlyInputBasic";
import {handleActiveError} from "../../../components/sonner/ToastBasic";
import SimmsInputSearch from "../../../components/input/SimmsInputSearch";
import SimmsDrawerSearch from "../../../components/drawer/simms/SimmsDrawerSearch";
import SearchSelector from "../../../components/searchSelector/SearchSelector";
import UserSelectionField from "../../../components/selection/UserSelectionField";
import {getDispose, updateDispose} from "../../../api/lifecycle/dispose/DisposeService";
import {selectCodesByParentId} from "../../../api/code/CodeService";

function DisposeUpdate(){
    const {id} = useParams();
    const dispatch = useDispatch();
    const [dispose, setDispose] = useState({});
    const [flatCode, setFlatCode] = useState([]);
    const [assets, setAssets] = useState([]);
    const approvalProcessId = useSelector((state) => state.content.approvalProcess?.id);

    const location = useLocation();
    const navigate = useNavigate();

    const goToDetail = () => navigate(`/lifecycle/dispose/${id}`);

    useEffect(() =>{
        Promise.all([
            getDispose(id),
            getContentStatus({contentId : id, contentCategory : ContentCategory.DISPOSE})
        ]).then(([ disposeResponse, statusNumericValueResponse]) => {
            setDispose(disposeResponse.data);
            setAssets(disposeResponse.data.assets.map(item => item.asset));

            if (statusNumericValueResponse && statusNumericValueResponse.status === 200) {
                const numericValue = statusNumericValueResponse.data.statusNumericValue;
                dispatch(changeStatusNumericValue(numericValue));
                dispatch(changeProgressStatusValue(statusNumericValueResponse.data));
            }

            return selectCodesByParentId(CodeSpecNum.DisposeRequestCode)
                .then(codes => {
                    setFlatCode(getFlatCode(codes));
                })
        }).finally(
            dispatch(skeleton(false))
        )
    }, [id, dispatch])

    useEffect(() => {
        clearApprovalProcessState(dispatch);
        return () => clearApprovalProcessState(dispatch);
    }, [dispatch, id]);

    useEffect(() => {
        if (!dispose.requestCode?.id || flatCode.length === 0) return;
        preloadApprovalProcessFromFlatCode(dispatch, dispose.requestCode.id, flatCode, { includeAfter: false });
    }, [dispose.requestCode?.id, flatCode, dispatch]);

    const requireds =["name","requester", "inspector", "requestCode", "requestStartDate", "requestEndDate"]
    const update = async ()=>{
        if(assets.length == 0){
            alert("폐기자산을 선택해주세요")
            return false;
        }

        if(requireds.some(e=> isEmpty(dispose[e]))){
            alert("필수값이 누락되었습니다.")
            return
        }

        dispatch(apiLoading(true));

        const assetData = assets?.map(item => ({asset : {id : item.id}}));
        dispose.assets = assetData;

        const data = {
            ...dispose,
            ...getApprovalProcessSubmitPayload(),
            requester: {id: dispose.requester.id},
            inspector: {id: dispose.inspector.id},
            requestCode: {id: dispose.requestCode.id}
        }

        await updateDispose(data).then(response => {
            if(response.status === 200){
                alert("접수되었습니다.")
                navigate("/lifecycle/dispose/"+id, {
                    state: {
                        test: location.state
                    }
                });
            }else if(response.status==201){
                handleActiveError({message: response.data.title, description: response.data.reason});
            }
        }).finally(dispatch(apiLoading(false)));
    }

    if (Object.keys(dispose).length > 0 && flatCode.length > 0) {
        const summaryMeta = [
            dispose.requestCode?.name,
            dispose.number && `번호 ${dispose.number}`,
        ]
            .filter(Boolean)
            .join(" · ");

        const requestInfoPanel = (
            <>
                <DetailPanel title="폐기자산">
                    <DetailKvTable>
                        <tbody>
                        <tr className="asset-detail-kv-row--auto">
                            <Th_title title={"자산목록"} colspan={3} className="fieldset-td relative align-top" required auth>
                                <UserSelectionField
                                    layout="inline"
                                    data={assets}
                                    countUnit="개"
                                    chipEntity="asset"
                                    listModalTitle="자산목록"
                                    onRemove={(v) => setAssets((prev) => prev.filter((asset) => asset.id !== v.id))}
                                >
                                    <SearchSelector
                                        auth
                                        multiSelect
                                        variant="compact"
                                        entity="dispose"
                                        uniqueKey="assets"
                                        data={assets}
                                        onChange={(v) => {
                                            if (v?.__multiFlat) setAssets(v.__multiFlat);
                                        }}
                                    >
                                        <SimmsInputSearch type={'HIDDEN'} target={"entityId"} value={dispose.id}/>
                                        <SimmsInputSearch type="TEXT"/>
                                        <SimmsDrawerSearch showLabel title="자산그룹" entity="code" target="group" parentKey={CodeSpecNum.AssetGroupCode} uniqueKey="group" recursive/>
                                        <SimmsDrawerSearch showLabel title="업무그룹" entity="code" target="task" parentKey={CodeSpecNum.TaskGroupCode} uniqueKey="task" recursive/>
                                        <SimmsDrawerSearch showLabel title="자산위치" entity="code" target="location" parentKey={CodeSpecNum.AssetLocation} uniqueKey="location" recursive/>
                                        <SimmsDrawerSearch showLabel title="운영담당자" entity="userAccount" target="operator"/>
                                        <SimmsDrawerSearch showLabel title="자산사용자" entity="userAccount" target="handler"/>
                                    </SearchSelector>
                                </UserSelectionField>
                            </Th_title>
                        </tr>
                        </tbody>
                    </DetailKvTable>
                </DetailPanel>

                <DetailPanel title="요청정보">
                    <DetailKvTable>
                        <tbody>
                        <tr>
                            <Th_title title={"요청자"} colspan={3} className="fieldset-td" required auth={dispose.auth.update}>
                                <Selector
                                    onChange={(v)=>onCreateClickListener(setDispose, "requester", dispose.requester ,v)}
                                    data={dispose.requester}
                                    entity={"userAccount"}
                                    uniqueKey={'requester'}>
                                    <SimmsInputSearch type={'TEXT'} placeHolder={"사용자명"} />
                                    <SimmsDrawerSearch showLabel title={'부서'} entity={'department'} target={'department'} recursive />
                                    <SimmsDrawerSearch showLabel title={'업체'} entity={'company'} target={'company'} />
                                </Selector>
                            </Th_title>
                        </tr>
                        <tr>
                            <Th_title title={"요청종류"} colspan={3} className="fieldset-td" required auth={dispose.auth.update}>
                                <Selector
                                    onChange={(v)=>onCreateClickListener(setDispose, "requestCode", dispose.requestCode ,v)}
                                    data={dispose.requestCode}
                                    entity={"code"}
                                    parentKey={CodeSpecNum.DisposeRequestCode}
                                    recursive
                                />
                            </Th_title>
                        </tr>
                        <tr>
                            <Th_title title={"요청일"} colspan={3} className="fieldset-td fieldset-td-input">
                                <div className="web-flex asset-detail-date-range">
                                    <SimmsDatePicker
                                        onChange={(v)=>dateOnChange(setDispose,"requestStartDate", v, false)}
                                        data={dispose.requestStartDate}
                                        constraint={{lessThan : dispose.requestEndDate && dayjs(dispose.requestEndDate), required: true}} auth={dispose.auth.update} />
                                    <span className="asset-detail-date-range-sep">~</span>
                                    <SimmsDatePicker
                                        onChange={(v)=>dateOnChange(setDispose,"requestEndDate", v, false)}
                                        data={dispose.requestEndDate}
                                        constraint={{largerThan : dispose.requestStartDate && dayjs(dispose.requestStartDate), required: true}} auth={dispose.auth.update} />
                                </div>
                            </Th_title>
                        </tr>
                        </tbody>
                    </DetailKvTable>
                </DetailPanel>
            </>
        );

        const workflowPanels = (
            <>
                <DetailPanel title="요청내용">
                    <DetailKvTable>
                        <tbody>
                        <tr>
                            <Th_title title={"요청번호"} colspan={3} className="fieldset-td">
                                <ReadOnlyInputBasic target={"number"} value={dispose.number} />
                            </Th_title>
                        </tr>
                        <tr>
                            <Th_title title={"검수자"} colspan={3} className="fieldset-td" auth={dispose.auth.update} required>
                                <Selector
                                    onChange={(v)=>onCreateClickListener(setDispose, "inspector", dispose.inspector ,v)}
                                    data={dispose.inspector}
                                    entity={"userAccount"}
                                    uniqueKey={'inspector'}>
                                    <SimmsInputSearch type={'TEXT'} placeHolder={"사용자명"} />
                                    <SimmsDrawerSearch showLabel title={'부서'} entity={'department'} target={'department'} recursive />
                                    <SimmsDrawerSearch showLabel title={'업체'} entity={'company'} target={'company'} />
                                </Selector>
                            </Th_title>
                        </tr>
                        <tr>
                            <Th_title title={"제목"} colspan={3} className="fieldset-td" auth={dispose.auth.update} required>
                                <SimmsInput onChange={(v)=>inputOnChange(setDispose,"name", v, true)} data={dispose.name} style={{width: "100%"}} />
                            </Th_title>
                        </tr>
                        <tr className="asset-detail-kv-row--auto">
                            <Th_title title={"내용"} colspan={3} className="fieldset-td" auth={dispose.auth.update}>
                                <SimmsTextArea onChange={(v)=>inputOnChange(setDispose,"content", v, false)}
                                               style={{width: "100%", minHeight: "80px"}}
                                               data={dispose.content} />
                            </Th_title>
                        </tr>
                        <tr className="asset-detail-kv-row--auto">
                            <Th_title title={"기타"} colspan={3} className="fieldset-td" auth={dispose.auth.update}>
                                <SimmsTextArea onChange={(v)=>inputOnChange(setDispose,"etc", v, false)}
                                               style={{width: "100%", minHeight: "80px"}}
                                               data={dispose.etc} />
                            </Th_title>
                        </tr>
                        </tbody>
                    </DetailKvTable>
                </DetailPanel>
            </>
        );

        const filePanel = (
            <>
                <ApprovalComponent auth={dispose.auth.update} approvalProcessId={approvalProcessId} />
                <DetailFilePanel
                    title="첨부"
                    isEditing={Boolean(dispose.auth?.update)}
                    tableInfo={{tableName: "Dispose", joinKey: dispose.id}}
                />
            </>
        );

        return (
            <div className="common-container container-height detail-list-layout asset-detail-list-layout border-l-gray flex min-h-0 flex-1 flex-col">
                <DetailFormTabChrome>
                    <ContentDetailInfoShell
                        title={dispose.name || "자산폐기 접수"}
                        meta={summaryMeta || undefined}
                        contentId={id}
                        contentCategory={ContentCategory.DISPOSE}
                        scrollMode="columns"
                        actions={
                            <DetailSummaryButton muted onClick={goToDetail}>
                                취소
                            </DetailSummaryButton>
                        }
                        rollup={{
                            update: dispose.auth?.update ? update : "",
                            updateTitle: "접수완료",
                        }}
                    >
                        <ContentDetailThreeColLayout
                            primary={requestInfoPanel}
                            secondary={workflowPanels}
                            aside={filePanel}
                        />
                    </ContentDetailInfoShell>
                </DetailFormTabChrome>
            </div>
        );
    }
}

export {DisposeUpdate}